import isEmpty from 'lodash/isEmpty'
import React, { useCallback, useContext, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import { CheckoutModalContext } from '@Domains/Checkout/Providers/CheckoutModalProvider'
import { PaymentError } from '@Domains/Checkout/Steps/Payment/Components/PaymentError/PaymentError'
import { PaymentResponseCallback } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { CheckoutV2FinalizeCartResult } from '@Pages/onestepcheckout'

export const useDefaultCheckoutResponse = () => {
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)
  const {
    setModal,
    unSetModal,
  } = useContext(CheckoutModalContext)
  const hasError = (res: CheckoutV2FinalizeCartResult) => !res || !res.success || !!res.error || !isEmpty(res.errors)

  const handleRedirect = useCallback((res: CheckoutV2FinalizeCartResult) => {
    if (res?.redirect && res.success) {
      window.location.href = storeURL('checkout/onepage/success')
      return true
    }
    return false
  }, [storeURL])

  const handleErrorResponse = useCallback((res: CheckoutV2FinalizeCartResult, paymentType: string) => {
    if (hasError(res)) {
      ampli.cofCompletionFailRes({
        payment_method: paymentType,
      })
      setModal(<PaymentError closeModal={unSetModal} error={t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')} />)
      return true
    }
    return false
  }, [setModal, t, unSetModal])

  const handlePaymentSubmitResponse: PaymentResponseCallback = useCallback((paymentType: string, finalizeCart: CheckoutV2FinalizeCartResult) => {
    let res: boolean = handleRedirect(finalizeCart)
    res = res || handleErrorResponse(finalizeCart, paymentType)
    return res
  }, [
    handleErrorResponse,
    handleRedirect,
  ])

  const value = useMemo(() => ({
    handleRedirect,
    handleErrorResponse,
    handlePaymentSubmitResponse,
  }), [
    handleErrorResponse,
    handlePaymentSubmitResponse,
    handleRedirect,
  ])

  return value
}
